import Link from 'next/link'
import { ChevronDown } from 'lucide-react'

interface FAQItem {
  question: string
  summary?: string
  answer: string
}

interface FAQProps {
  faqs: FAQItem[]
  openFaq: number | null
  toggleFaq: (index: number) => void
  handleFaqKeyDown: (e: React.KeyboardEvent, index: number) => void
  isVisible: boolean
}

export default function FAQ({ faqs, openFaq, toggleFaq, handleFaqKeyDown, isVisible }: FAQProps) {
  return (
    <section
      className={`w-full bg-gradient-to-b from-white to-slate-50 px-4 py-16 transition-all duration-700 sm:px-6 md:py-24 lg:px-8 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
    >
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <span className="inline-block rounded-full bg-blue-100 px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">
            FAQ
          </span>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
            자주 묻는 질문
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-gray-600 md:text-base">
            질문을 클릭하면 답변이 펼쳐집니다. 원하는 답이 없다면{' '}
            <Link
              href="/inquiries/new"
              data-landing-event="click_public_inquiry_channel"
              className="font-medium text-blue-600 underline hover:text-blue-700"
            >
              공개 문의 채널
            </Link>
            로 바로 문의해주세요.
          </p>
        </div>

        <div className="mt-12 space-y-4 md:mt-16">
          {faqs.map((faq, index) => {
            const isOpen = openFaq === index
            const questionId = `faq-question-${index}`
            const answerId = `faq-answer-${index}`

            return (
              <div
                key={`${faq.question}-${index}`}
                className={`group overflow-hidden rounded-2xl border transition-all duration-300 ${
                  isOpen
                    ? 'border-blue-300 bg-blue-50/40 shadow-lg'
                    : 'border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:shadow-md'
                }`}
              >
                <button
                  id={questionId}
                  onClick={() => toggleFaq(index)}
                  onKeyDown={(e) => handleFaqKeyDown(e, index)}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  className={`flex min-h-[64px] w-full flex-col items-start rounded-2xl px-6 py-5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 md:px-8 md:py-6 ${
                    isOpen ? 'bg-blue-50/60 hover:bg-blue-50/80' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex w-full items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="mt-0.5 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs font-semibold text-gray-700">
                        {index + 1}
                      </span>
                      <span className="text-base font-semibold leading-relaxed text-gray-900 md:text-lg lg:text-xl">
                        {faq.question}
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-6 w-6 flex-shrink-0 transition-all duration-300 ${
                        isOpen ? 'rotate-180 text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                      }`}
                      aria-hidden="true"
                    />
                  </div>

                  {faq.summary && (
                    <p className={`mt-2 pl-9 text-sm leading-relaxed ${isOpen ? 'text-gray-600' : 'text-gray-500'}`}>
                      {faq.summary}
                    </p>
                  )}
                </button>

                {isOpen && (
                  <div
                    id={answerId}
                    role="region"
                    aria-labelledby={questionId}
                    className="border-t border-blue-200 bg-gradient-to-b from-white to-gray-50 px-6 py-5 text-base leading-7 text-gray-700 md:px-8 md:py-6 md:text-lg"
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
